import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Video } from '@sorare/core/src/atoms/ui/Video';
import { isType } from '@sorare/core/src/gql';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { FlowEndButton } from 'components/so5/LastFixtureStory/FlowEndButton';
import { GameweekDate } from 'components/so5/LastFixtureStory/GameweekDate';
import { useResultsStoryAssets } from 'components/so5/LastFixtureStory/useResultsStoryAssets';

import { Intro_so5Fixture } from './__generated__/index.graphql';
import { usePlayVideoManuallyOnError } from './usePlayVideoManuallyOnError';

const titleEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(50%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;

const gameweekEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-50%);
  }
  to {
    opacity: 1;
    transform: translateY(0%);
  }
`;
const buttonsEnter = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const contentsExit = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

export const Title = styled.div`
  font: var(--t-32);
  text-align: center;
  animation: ${titleEnter} 1s ease-out forwards;
  @media ${tabletAndAbove} {
    font: var(--t-48);
  }
`;

const GameweekDateWrapper = styled(Vertical).attrs({ center: true })`
  animation: ${gameweekEnter} 2s ease-out forwards;
`;

const VideoPlayer = styled(Video)`
  position: absolute;
  inset: 0;
`;
const BgVideo = styled(VideoPlayer)`
  opacity: 0;
  &.display {
    opacity: 1;
  }
`;

const StyledTitle = styled(Title)`
  /* center the title */
  margin-bottom: 40px;
`;

const Buttons = styled(Vertical)`
  position: absolute;
  min-width: 200px;
  bottom: var(--quadruple-unit);
  opacity: 0;
  animation: ${buttonsEnter} 1s ease-out 1.7s forwards;
`;

const Contents = styled(Vertical).attrs({ gap: 1, center: true })`
  isolation: isolate;
  &.isUnmounting {
    animation: ${contentsExit} 0.5s ease-out forwards;
  }
`;

type Props = {
  onClose: () => void;
  onNext?: () => void;
  so5Fixture: Intro_so5Fixture;
};

export const Intro = ({ onClose, onNext, so5Fixture }: Props) => {
  const { toIntro, introOut, introLoop } = useResultsStoryAssets();
  const [displayBgVideo, setDisplayBgVideo] = useState(false);
  const unclaimedRewards = useRef(
    so5Fixture && isType(so5Fixture, 'So5Fixture')
      ? so5Fixture.mySo5Rewards?.filter(
          reward => reward.aasmState !== 'claimed'
        )
      : []
  );
  const [isUnmounting, setIsUnmounting] = useState(false);
  const hasRewards = unclaimedRewards.current?.length > 0;
  const actionAfterOutro = (action?: () => void) => {
    setIsUnmounting(true);
    if (!action) return;
    setTimeout(action, 1000);
  };
  const introVideoRef = useRef<HTMLVideoElement>(null);
  const bgVideoRef = useRef<HTMLVideoElement>(null);
  const bgVideo = isUnmounting ? introOut : introLoop;
  const { onVideoError } = usePlayVideoManuallyOnError(introVideoRef);
  return (
    <>
      <VideoPlayer
        ref={introVideoRef}
        hideMutedIcon
        autoPlay
        muted
        playsInline
        onError={onVideoError}
        onEnded={() => {
          setDisplayBgVideo(true);
          bgVideoRef.current?.play();
        }}
        sources={toIntro}
      />
      <BgVideo
        key={bgVideo[0]?.src}
        ref={bgVideoRef}
        hideMutedIcon
        muted
        autoPlay={isUnmounting}
        loop={!isUnmounting}
        playsInline
        className={classNames({
          display: displayBgVideo,
        })}
        sources={bgVideo}
      />
      <Contents
        className={classNames({
          isUnmounting,
        })}
      >
        <GameweekDateWrapper>
          <GameweekDate
            startDate={so5Fixture.startDate}
            endDate={so5Fixture.endDate}
          />
        </GameweekDateWrapper>
        <StyledTitle>
          {hasRewards ? (
            <FormattedMessage
              id="LastFixtureStory.titleWithRewards"
              defaultMessage="Claim your glory"
            />
          ) : (
            <FormattedMessage
              id="LastFixtureStory.title"
              defaultMessage="New Pro Results"
            />
          )}
        </StyledTitle>
        <Buttons gap={3} center>
          {onNext ? (
            <Button size="medium" fullWidth color="secondary" onClick={onNext}>
              <FormattedMessage
                id="LastFixtureStory.next"
                defaultMessage="Enter"
              />
            </Button>
          ) : (
            <FlowEndButton
              onClose={() => actionAfterOutro(onClose)}
              so5Fixture={so5Fixture}
              onClick={actionAfterOutro}
            />
          )}
          <Button
            size="medium"
            color="tertiary"
            onClick={() => actionAfterOutro(onClose)}
          >
            <FormattedMessage {...glossary.close} />
          </Button>
        </Buttons>
      </Contents>
    </>
  );
};

Intro.fragments = {
  so5Fixture: gql`
    fragment Intro_so5Fixture on So5Fixture {
      slug
      startDate
      endDate
      ...FlowEndButton_so5Fixture
    }
    ${FlowEndButton.fragments.so5Fixture}
  ` as TypedDocumentNode<Intro_so5Fixture>,
};
